import React, { useState, useCallback } from 'react';
import {
  Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Eye, Code,
  Type, Image, MousePointerClick, Quote, Columns, Minus, Heading, AlignCenter, Sparkles, Loader2
} from 'lucide-react';
import apiService from '../services/api';

const BLOCK_TYPES = [
  { type: 'header_image', label: 'Header Image', icon: Image },
  { type: 'heading', label: 'Heading', icon: Heading },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'button', label: 'Button', icon: MousePointerClick },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'divider', label: 'Divider', icon: Minus },
  { type: 'quote', label: 'Quote', icon: Quote },
  { type: 'two_column', label: 'Two Columns', icon: Columns },
  { type: 'spacer', label: 'Spacer', icon: AlignCenter },
];

function defaultBlock(type) {
  switch (type) {
    case 'header_image': return { type, src: '', alt: 'Amour Mobile Nails' };
    case 'heading': return { type, text: '' };
    case 'text': return { type, text: '' };
    case 'button': return { type, text: 'Book Now', url: 'tel:3213209044' };
    case 'image': return { type, src: '', alt: '', width: '600' };
    case 'divider': return { type };
    case 'quote': return { type, text: '', author: '' };
    case 'two_column': return { type, left_html: '', right_html: '' };
    case 'spacer': return { type, height: '20' };
    default: return { type };
  }
}

/**
 * Normalize a block from nested content format to flat format.
 * Handles both {"type": "heading", "content": {"text": "..."}}
 * and already-flat {"type": "heading", "text": "..."}.
 */
export function normalizeBlock(block) {
  if (!block || !block.content || typeof block.content !== 'object') return block;
  const { content, order, ...rest } = block;
  // Merge content fields into top-level, preserving type and any existing top-level fields.
  // Map "url" to "src" for image block types since the backend renderer accepts both.
  const merged = { ...rest };
  for (const [key, val] of Object.entries(content)) {
    if (key === 'url' && (block.type === 'header_image' || block.type === 'image' || block.type === 'logo')) {
      merged.src = merged.src || val;
    } else {
      merged[key] = val;
    }
  }
  return merged;
}

export function normalizeBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  return blocks.map(normalizeBlock);
}

function AiImageButton({ onGenerated }) {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await apiService.generateImage(prompt.trim());
      onGenerated(result.url);
      setPrompt('');
      setShowInput(false);
    } catch (err) {
      setError(err.message || 'Generation failed');
    }
    setGenerating(false);
  };

  if (!showInput) {
    return (
      <button type="button" onClick={() => setShowInput(true)}
        className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100 transition-colors">
        <Sparkles className="w-3 h-3" /> Generate with AI
      </button>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <input type="text" value={prompt} onChange={e => setPrompt(e.target.value)}
          placeholder="e.g. spring flowers, pink nails, elegant spa..."
          className="flex-1 px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-purple-500"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), generate())}
          disabled={generating} />
        <button type="button" onClick={generate} disabled={generating || !prompt.trim()}
          className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
          {generating ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</> : <><Sparkles className="w-3 h-3" /> Go</>}
        </button>
        <button type="button" onClick={() => { setShowInput(false); setError(null); }}
          className="px-1.5 py-1 text-xs text-gray-400 hover:text-gray-600">
          &times;
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {generating && <p className="text-xs text-purple-500">DALL-E is generating your image... (~10s)</p>}
    </div>
  );
}

function BlockEditor({ block, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const update = (field, value) => onChange(index, { ...block, [field]: value });

  return (
    <div className="border border-gray-200 rounded bg-white group relative">
      <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border-b border-gray-200 text-xs">
        <GripVertical className="w-3 h-3 text-gray-300" />
        <span className="font-medium text-gray-600 uppercase tracking-wide flex-1">
          {BLOCK_TYPES.find(b => b.type === block.type)?.label || block.type}
        </span>
        <button onClick={() => onMoveUp(index)} disabled={isFirst}
          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
          <ChevronUp className="w-3 h-3" />
        </button>
        <button onClick={() => onMoveDown(index)} disabled={isLast}
          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
          <ChevronDown className="w-3 h-3" />
        </button>
        <button onClick={() => onRemove(index)}
          className="p-0.5 text-gray-400 hover:text-red-500">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="p-2 space-y-1.5">
        {block.type === 'header_image' && (
          <>
            <input type="text" value={block.src || ''} onChange={e => update('src', e.target.value)}
              placeholder="Image URL (e.g. https://...)" className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
            {block.src && <img src={block.src} alt={block.alt || ''} className="w-full rounded mt-1" style={{ maxHeight: '120px', objectFit: 'cover' }} />}
            <input type="text" value={block.alt || ''} onChange={e => update('alt', e.target.value)}
              placeholder="Alt text" className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
            <AiImageButton onGenerated={(url) => update('src', url)} />
          </>
        )}
        {block.type === 'heading' && (
          <input type="text" value={block.text || ''} onChange={e => update('text', e.target.value)}
            placeholder="Heading text..." className="w-full px-2 py-1 text-sm border rounded outline-none focus:ring-1 focus:ring-red-800"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: 'italic', color: '#B22222' }} />
        )}
        {block.type === 'text' && (
          <textarea value={block.text || ''} onChange={e => update('text', e.target.value)}
            placeholder="Body text... (HTML allowed for links, bold, etc.)" rows={3}
            className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
        )}
        {block.type === 'button' && (
          <div className="grid grid-cols-2 gap-1.5">
            <input type="text" value={block.text || ''} onChange={e => update('text', e.target.value)}
              placeholder="Button text" className="px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
            <input type="text" value={block.url || ''} onChange={e => update('url', e.target.value)}
              placeholder="URL or tel:..." className="px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
          </div>
        )}
        {block.type === 'image' && (
          <>
            <input type="text" value={block.src || ''} onChange={e => update('src', e.target.value)}
              placeholder="Image URL" className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
            {block.src && <img src={block.src} alt={block.alt || ''} className="w-full rounded mt-1" style={{ maxHeight: '120px', objectFit: 'cover' }} />}
            <div className="grid grid-cols-2 gap-1.5">
              <input type="text" value={block.alt || ''} onChange={e => update('alt', e.target.value)}
                placeholder="Alt text" className="px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
              <input type="text" value={block.width || '600'} onChange={e => update('width', e.target.value)}
                placeholder="Width (px)" className="px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
            </div>
            <AiImageButton onGenerated={(url) => update('src', url)} />
          </>
        )}
        {block.type === 'divider' && (
          <div className="text-center py-1"><hr className="border-gray-300" /></div>
        )}
        {block.type === 'quote' && (
          <>
            <textarea value={block.text || ''} onChange={e => update('text', e.target.value)}
              placeholder="Quote text..." rows={2}
              className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800 italic" />
            <input type="text" value={block.author || ''} onChange={e => update('author', e.target.value)}
              placeholder="Author name" className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
          </>
        )}
        {block.type === 'two_column' && (
          <div className="grid grid-cols-2 gap-1.5">
            <textarea value={block.left_html || ''} onChange={e => update('left_html', e.target.value)}
              placeholder="Left column HTML..." rows={3}
              className="px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800 font-mono" />
            <textarea value={block.right_html || ''} onChange={e => update('right_html', e.target.value)}
              placeholder="Right column HTML..." rows={3}
              className="px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800 font-mono" />
          </div>
        )}
        {block.type === 'spacer' && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Height:</label>
            <input type="number" value={block.height || 20} onChange={e => update('height', e.target.value)}
              className="w-20 px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" min="5" max="100" />
            <span className="text-xs text-gray-400">px</span>
          </div>
        )}
      </div>
    </div>
  );
}

function EmailPreview({ blocks }) {
  const renderBlock = (rawBlock, i) => {
    const block = normalizeBlock(rawBlock);
    switch (block.type) {
      case 'header_image':
        return block.src ? (
          <div key={i} style={{ textAlign: 'center' }}>
            <img src={block.src} alt={block.alt || ''} style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }} />
          </div>
        ) : (
          <div key={i} style={{ textAlign: 'center', padding: '40px 20px', background: '#f5f5f5', color: '#999', fontSize: '14px' }}>
            Header image placeholder
          </div>
        );
      case 'heading':
        return (
          <div key={i} style={{ textAlign: 'center', padding: '20px 40px 10px', fontFamily: "Georgia, 'Times New Roman', serif", fontSize: '26px', fontStyle: 'italic', color: '#B22222', lineHeight: 1.3 }}>
            {block.text || 'Heading'}
          </div>
        );
      case 'text':
        return (
          <div key={i} style={{ textAlign: 'center', padding: '10px 40px', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '16px', color: '#555555', lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: block.text || 'Body text' }} />
        );
      case 'button':
        return (
          <div key={i} style={{ textAlign: 'center', padding: '20px 40px' }}>
            <span style={{ backgroundColor: '#A92A2A', color: '#F1E3B2', fontFamily: "Georgia, 'Times New Roman', serif", fontSize: '18px', padding: '14px 40px', textDecoration: 'none', borderRadius: '3px', display: 'inline-block' }}>
              {block.text || 'Button'}
            </span>
          </div>
        );
      case 'image':
        return block.src ? (
          <div key={i} style={{ textAlign: 'center', padding: '10px 0' }}>
            <img src={block.src} alt={block.alt || ''} style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }} width={block.width || 600} />
          </div>
        ) : (
          <div key={i} style={{ textAlign: 'center', padding: '20px', background: '#f5f5f5', color: '#999', fontSize: '14px' }}>
            Image placeholder
          </div>
        );
      case 'divider':
        return (
          <div key={i} style={{ textAlign: 'center', padding: '20px 0' }}>
            <hr style={{ border: 'none', borderTop: '1px solid #E0E0E0', width: '80%', margin: '0 auto' }} />
          </div>
        );
      case 'quote':
        return (
          <div key={i} style={{ textAlign: 'center', padding: '15px 50px', fontFamily: "Georgia, 'Times New Roman', serif", fontSize: '16px', fontStyle: 'italic', color: '#666', lineHeight: 1.5 }}>
            "{block.text || '...'}"
            {block.author && <div style={{ fontSize: '14px', color: '#999', marginTop: '5px' }}>&mdash; {block.author}</div>}
          </div>
        );
      case 'two_column':
        return (
          <div key={i} style={{ padding: '10px 20px' }}>
            <table width="100%" cellPadding="0" cellSpacing="0"><tbody><tr>
              <td width="50%" valign="top" style={{ padding: '10px', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '14px', color: '#555' }}
                dangerouslySetInnerHTML={{ __html: block.left_html || 'Left' }} />
              <td width="50%" valign="top" style={{ padding: '10px', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '14px', color: '#555' }}
                dangerouslySetInnerHTML={{ __html: block.right_html || 'Right' }} />
            </tr></tbody></table>
          </div>
        );
      case 'spacer':
        return <div key={i} style={{ height: `${block.height || 20}px` }} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ background: '#F2F2F2', padding: '20px 0' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', background: '#FFFFFF' }}>
        {blocks.map((b, i) => renderBlock(b, i))}
        {blocks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999', fontSize: '14px' }}>
            Add blocks to build your email
          </div>
        )}
      </div>
    </div>
  );
}

export default function EmailBlockEditor({ blocks, onChange, templates, onLoadTemplate }) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview' | 'html'
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const addBlock = useCallback((type) => {
    onChange([...blocks, defaultBlock(type)]);
    setShowAddMenu(false);
  }, [blocks, onChange]);

  const updateBlock = useCallback((index, updated) => {
    const next = [...blocks];
    next[index] = updated;
    onChange(next);
  }, [blocks, onChange]);

  const removeBlock = useCallback((index) => {
    onChange(blocks.filter((_, i) => i !== index));
  }, [blocks, onChange]);

  const moveUp = useCallback((index) => {
    if (index === 0) return;
    const next = [...blocks];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  }, [blocks, onChange]);

  const moveDown = useCallback((index) => {
    if (index === blocks.length - 1) return;
    const next = [...blocks];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange(next);
  }, [blocks, onChange]);

  // Generate raw HTML for the code view
  const generatePreviewHtml = () => {
    const sections = blocks.map(block => {
      switch (block.type) {
        case 'header_image':
          return block.src
            ? `<tr><td align="center" style="padding:0"><img src="${block.src}" alt="${block.alt || ''}" width="600" style="max-width:100%;height:auto;display:block" /></td></tr>`
            : '';
        case 'heading':
          return `<tr><td align="center" style="padding:20px 40px 10px;font-family:Georgia,'Times New Roman',serif;font-size:26px;font-style:italic;color:#B22222;line-height:1.3">${block.text || ''}</td></tr>`;
        case 'text':
          return `<tr><td align="center" style="padding:10px 40px;font-family:Helvetica,Arial,sans-serif;font-size:16px;color:#555555;line-height:1.6">${block.text || ''}</td></tr>`;
        case 'button':
          return `<tr><td align="center" style="padding:20px 40px"><a href="${block.url || '#'}" style="background-color:#A92A2A;color:#F1E3B2;font-family:Georgia,'Times New Roman',serif;font-size:18px;padding:14px 40px;text-decoration:none;border-radius:3px;display:inline-block">${block.text || 'Button'}</a></td></tr>`;
        case 'image':
          return block.src
            ? `<tr><td align="center" style="padding:10px 0"><img src="${block.src}" alt="${block.alt || ''}" width="${block.width || 600}" style="max-width:100%;height:auto;display:block" /></td></tr>`
            : '';
        case 'divider':
          return '<tr><td align="center" style="padding:20px 0"><hr style="border:none;border-top:1px solid #E0E0E0;width:80%" /></td></tr>';
        case 'quote': {
          const authorHtml = block.author ? `<br/><em style="font-size:14px;color:#999">&mdash; ${block.author}</em>` : '';
          return `<tr><td align="center" style="padding:15px 50px;font-family:Georgia,'Times New Roman',serif;font-size:16px;font-style:italic;color:#666;line-height:1.5">"${block.text || ''}"${authorHtml}</td></tr>`;
        }
        case 'two_column':
          return `<tr><td style="padding:10px 20px"><table width="100%" cellpadding="0" cellspacing="0"><tr><td width="50%" valign="top" style="padding:10px;font-family:Helvetica,Arial,sans-serif;font-size:14px;color:#555">${block.left_html || ''}</td><td width="50%" valign="top" style="padding:10px;font-family:Helvetica,Arial,sans-serif;font-size:14px;color:#555">${block.right_html || ''}</td></tr></table></td></tr>`;
        case 'spacer':
          return `<tr><td style="padding:0;height:${block.height || 20}px;line-height:${block.height || 20}px">&nbsp;</td></tr>`;
        default:
          return '';
      }
    }).filter(Boolean).join('\n');

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background-color:#F2F2F2;font-family:Helvetica,Arial,sans-serif"><table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F2F2F2"><tr><td align="center" style="padding:20px 0"><table width="600" cellpadding="0" cellspacing="0" style="background-color:#FFFFFF;max-width:600px;width:100%">\n${sections}\n</table></td></tr></table></body></html>`;
  };

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-gray-700">Email Content</label>
        <div className="flex gap-1">
          {templates && templates.length > 0 && (
            <button type="button" onClick={() => setShowTemplatePicker(!showTemplatePicker)}
              className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 text-gray-600">
              Templates
            </button>
          )}
          <button type="button" onClick={() => setViewMode('edit')}
            className={`px-2 py-0.5 text-xs border rounded ${viewMode === 'edit' ? 'bg-red-800 text-white border-red-800' : 'hover:bg-gray-50 text-gray-600'}`}>
            Edit
          </button>
          <button type="button" onClick={() => setViewMode('preview')}
            className={`px-2 py-0.5 text-xs border rounded ${viewMode === 'preview' ? 'bg-red-800 text-white border-red-800' : 'hover:bg-gray-50 text-gray-600'}`}>
            <Eye className="w-3 h-3 inline mr-0.5" />Preview
          </button>
          <button type="button" onClick={() => setViewMode('html')}
            className={`px-2 py-0.5 text-xs border rounded ${viewMode === 'html' ? 'bg-red-800 text-white border-red-800' : 'hover:bg-gray-50 text-gray-600'}`}>
            <Code className="w-3 h-3 inline mr-0.5" />HTML
          </button>
        </div>
      </div>

      {/* Template Picker */}
      {showTemplatePicker && (
        <div className="mb-3 p-2 bg-gray-50 border rounded">
          <p className="text-xs text-gray-500 mb-1.5">Load blocks from a template:</p>
          <div className="flex flex-wrap gap-1.5">
            {templates.map(t => (
              <button key={t.id} type="button"
                onClick={() => { onLoadTemplate(t); setShowTemplatePicker(false); }}
                className="px-2 py-1 text-xs border rounded bg-white hover:border-red-800 hover:text-red-800 transition-colors">
                {t.name}
                {t.is_default && <span className="ml-1 text-gray-400">(default)</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Edit mode */}
      {viewMode === 'edit' && (
        <div className="space-y-2">
          {blocks.map((block, i) => (
            <BlockEditor key={i} block={block} index={i}
              onChange={updateBlock} onRemove={removeBlock}
              onMoveUp={moveUp} onMoveDown={moveDown}
              isFirst={i === 0} isLast={i === blocks.length - 1} />
          ))}

          {/* Add block button */}
          <div className="relative">
            <button type="button" onClick={() => setShowAddMenu(!showAddMenu)}
              className="w-full py-2 border-2 border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-red-800 hover:text-red-800 transition-colors flex items-center justify-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add Block
            </button>
            {showAddMenu && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 p-2 grid grid-cols-3 gap-1">
                {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
                  <button key={type} type="button" onClick={() => addBlock(type)}
                    className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-700 hover:bg-red-50 hover:text-red-800 rounded transition-colors">
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview mode */}
      {viewMode === 'preview' && (
        <div className="border rounded overflow-hidden" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <EmailPreview blocks={blocks} />
        </div>
      )}

      {/* HTML mode */}
      {viewMode === 'html' && (
        <textarea readOnly value={generatePreviewHtml()}
          className="w-full px-3 py-1.5 text-xs border rounded font-mono bg-gray-50 text-gray-700 outline-none"
          rows={12} />
      )}
    </div>
  );
}
